import styled from '@emotion/styled';
import { Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import { Decor, Hot, Logo, Poster, PosterHeader, Subtitle, Title, Titles, useKeyText } from './scheme-parts';

/**
 * PLAYOFF_BRACKET: one round column with a pair in it. The keys sit on the
 * first pair — the upper side holds a decided team, the lower one a team not
 * decided yet, so the slot placeholder has its own place.
 */
const BracketScheme: React.FC = () => {
  const text = useKeyText();
  const shield = (
    <Logo round>
      <Shield strokeWidth={1.5} />
    </Logo>
  );
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
          <Hot k={PlaceholderKey.STAGE_NAME}>
            <Subtitle>{text(PlaceholderKey.STAGE_NAME)}</Subtitle>
          </Hot>
        </Titles>
      </PosterHeader>
      <Hot k={PlaceholderKey.ROUND_NAME}>{text(PlaceholderKey.ROUND_NAME)}</Hot>
      <Pair>
        <Side>
          <Hot k={PlaceholderKey.TEAM_LOGO}>{shield}</Hot>
          <Names>
            <Hot k={PlaceholderKey.TEAM_NAME}>
              <b>{text(PlaceholderKey.TEAM_NAME)}</b>
            </Hot>
            <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
          </Names>
          <Hot k={PlaceholderKey.SCORE_TEAM}>{text(PlaceholderKey.SCORE_TEAM)}</Hot>
          <Hot k={PlaceholderKey.SCORE_PENALTY_TEAM}>({text(PlaceholderKey.SCORE_PENALTY_TEAM)})</Hot>
        </Side>
        <Side>
          {shield}
          <Names>
            <Hot k={PlaceholderKey.TEAM_SLOT_PLACEHOLDER}>{text(PlaceholderKey.TEAM_SLOT_PLACEHOLDER)}</Hot>
          </Names>
          <Decor>—</Decor>
        </Side>
        <Footer>
          <Hot k={PlaceholderKey.SCORE}>{text(PlaceholderKey.SCORE)}</Hot>
          <Hot k={PlaceholderKey.MATCH_DATE}>{text(PlaceholderKey.MATCH_DATE)}</Hot>
          <Hot k={PlaceholderKey.MATCH_TIME}>{text(PlaceholderKey.MATCH_TIME)}</Hot>
          <Hot k={PlaceholderKey.MATCH_VENUE}>{text(PlaceholderKey.MATCH_VENUE)}</Hot>
        </Footer>
      </Pair>
      <Pair>
        <Side>
          {shield}
          <Decor>{text(PlaceholderKey.TEAM_NAME, 1)}</Decor>
        </Side>
        <Side>
          {shield}
          <Decor>{text(PlaceholderKey.TEAM_NAME, 2)}</Decor>
        </Side>
      </Pair>
    </Poster>
  );
};

export default BracketScheme;

const Pair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Names = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
